import { useState, useEffect } from 'react';
import {
  Settings2,
  Plus,
  Pencil,
  Trash2,
  Search,
  Check,
  X,
  ChevronDown,
  ChevronRight,
  MapPin,
  Gauge,
  Fuel,
  Activity,
  Zap,
  Signal,
  Bluetooth,
  Hexagon,
  FileText,
  Route,
  Leaf,
  ToggleLeft,
  ToggleRight,
  Star,
  Crown,
  Plug,
  RefreshCw,
  Battery,
} from 'lucide-react';
import { adminApi } from '../../services/api';

interface TrackerParameter {
  id: string;
  avlId: number | null;
  code: string;
  name: string;
  description: string | null;
  category: string;
  subcategory: string | null;
  dataType: string;
  unit: string | null;
  minValue: number | null;
  maxValue: number | null;
  icon: string | null;
  color: string | null;
  sortOrder: number;
  isActive: boolean;
  isDefault: boolean;
  isPremium: boolean;
  requiresObd: boolean;
  requiresBluetooth: boolean;
  createdAt: string;
  updatedAt: string;
}

interface Plan {
  id: string;
  name: string;
  displayName: string;
}

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  POSITION: <MapPin className="h-5 w-5" />,
  VEHICLE_OBD: <Gauge className="h-5 w-5" />,
  ENGINE: <Activity className="h-5 w-5" />,
  FUEL: <Fuel className="h-5 w-5" />,
  DRIVER_BEHAVIOR: <Leaf className="h-5 w-5" />,
  ACCELEROMETER: <Activity className="h-5 w-5" />,
  NETWORK: <Signal className="h-5 w-5" />,
  POWER: <Zap className="h-5 w-5" />,
  EV: <Battery className="h-5 w-5" />,
  BLUETOOTH: <Bluetooth className="h-5 w-5" />,
  GEOFENCING: <Hexagon className="h-5 w-5" />,
  DIAGNOSTICS: <FileText className="h-5 w-5" />,
  TRIPS: <Route className="h-5 w-5" />,
};

const CATEGORY_COLORS: Record<string, string> = {
  POSITION: 'bg-emerald-100 text-emerald-700',
  VEHICLE_OBD: 'bg-blue-100 text-blue-700',
  ENGINE: 'bg-red-100 text-red-700',
  FUEL: 'bg-amber-100 text-amber-700',
  DRIVER_BEHAVIOR: 'bg-green-100 text-green-700',
  ACCELEROMETER: 'bg-purple-100 text-purple-700',
  NETWORK: 'bg-cyan-100 text-cyan-700',
  POWER: 'bg-yellow-100 text-yellow-700',
  EV: 'bg-lime-100 text-lime-700',
  BLUETOOTH: 'bg-indigo-100 text-indigo-700',
  GEOFENCING: 'bg-teal-100 text-teal-700',
  DIAGNOSTICS: 'bg-slate-100 text-slate-700',
  TRIPS: 'bg-violet-100 text-violet-700',
};

const CATEGORY_LABELS: Record<string, string> = {
  POSITION: 'Position & Navigation',
  VEHICLE_OBD: 'Véhicule OBD',
  ENGINE: 'Moteur',
  FUEL: 'Carburant',
  DRIVER_BEHAVIOR: 'Comportement conducteur',
  ACCELEROMETER: 'Accéléromètre',
  NETWORK: 'Réseau GSM',
  POWER: 'Alimentation',
  EV: 'Véhicule Électrique',
  BLUETOOTH: 'Bluetooth',
  GEOFENCING: 'Geofencing',
  DIAGNOSTICS: 'Diagnostics',
  TRIPS: 'Trajets & Kilométrage',
};

const CATEGORIES = [
  'POSITION', 'VEHICLE_OBD', 'ENGINE', 'FUEL', 'DRIVER_BEHAVIOR',
  'ACCELEROMETER', 'NETWORK', 'POWER', 'EV', 'BLUETOOTH', 'GEOFENCING',
  'DIAGNOSTICS', 'TRIPS'
];

export default function TrackerParametersPage() {
  const [parameters, setParameters] = useState<TrackerParameter[]>([]);
  const [groupedParameters, setGroupedParameters] = useState<Record<string, TrackerParameter[]>>({});
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [editingParam, setEditingParam] = useState<TrackerParameter | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [planParameters, setPlanParameters] = useState<Set<string>>(new Set());
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(CATEGORIES));
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [filterType, setFilterType] = useState<'all' | 'default' | 'premium'>('all');

  // Form state
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    category: 'POSITION',
    subcategory: '',
    dataType: 'number',
    unit: '',
    avlId: '',
    icon: '',
    color: '#10b981',
    sortOrder: 0,
    isActive: true,
    isDefault: false,
    isPremium: false,
    requiresObd: false,
    requiresBluetooth: false,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [paramsRes, plansRes] = await Promise.all([
        adminApi.getTrackerParameters(),
        adminApi.getPlans(),
      ]);

      const params = paramsRes.data?.data || paramsRes.data || [];
      setParameters(params);
      setGroupedParameters(paramsRes.data?.grouped || {});
      setPlans(plansRes.data || []);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const openCreateModal = () => {
    setEditingParam(null);
    setFormData({
      code: '',
      name: '',
      description: '',
      category: 'POSITION',
      subcategory: '',
      dataType: 'number',
      unit: '',
      avlId: '',
      icon: '',
      color: '#10b981',
      sortOrder: 0,
      isActive: true,
      isDefault: false,
      isPremium: false,
      requiresObd: false,
      requiresBluetooth: false,
    });
    setShowModal(true);
  };

  const openEditModal = (param: TrackerParameter) => {
    setEditingParam(param);
    setFormData({
      code: param.code,
      name: param.name,
      description: param.description || '',
      category: param.category,
      subcategory: param.subcategory || '',
      dataType: param.dataType,
      unit: param.unit || '',
      avlId: param.avlId?.toString() || '',
      icon: param.icon || '',
      color: param.color || '#10b981',
      sortOrder: param.sortOrder,
      isActive: param.isActive,
      isDefault: param.isDefault,
      isPremium: param.isPremium,
      requiresObd: param.requiresObd,
      requiresBluetooth: param.requiresBluetooth,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        avlId: formData.avlId ? parseInt(formData.avlId) : null,
      };

      if (editingParam) {
        await adminApi.updateTrackerParameter(editingParam.id, payload);
      } else {
        await adminApi.createTrackerParameter(payload);
      }

      setShowModal(false);
      loadData();
    } catch (error: any) {
      console.error('Failed to save parameter:', error);
      alert(error.response?.data?.error || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (param: TrackerParameter) => {
    if (!confirm(`Supprimer le paramètre "${param.name}" ?`)) return;
    try {
      await adminApi.deleteTrackerParameter(param.id);
      loadData();
    } catch (error) {
      console.error('Failed to delete parameter:', error);
    }
  };

  const handleToggleActive = async (param: TrackerParameter) => {
    try {
      await adminApi.updateTrackerParameter(param.id, { isActive: !param.isActive });
      loadData();
    } catch (error) {
      console.error('Failed to toggle parameter:', error);
    }
  };

  const openPlanModal = async (plan: Plan) => {
    setSelectedPlan(plan);
    try {
      const res = await adminApi.getPlanParameters(plan.id);
      const enabledIds = (res.data?.parameters || [])
        .filter((p: any) => p.isEnabledForPlan)
        .map((p: any) => p.id);
      setPlanParameters(new Set(enabledIds));
      setShowPlanModal(true);
    } catch (error) {
      console.error('Failed to load plan parameters:', error);
    }
  };

  const handleTogglePlanParameter = async (parameterId: string) => {
    if (!selectedPlan) return;
    const isEnabled = planParameters.has(parameterId);
    try {
      await adminApi.togglePlanParameter(selectedPlan.id, parameterId, !isEnabled);
      setPlanParameters((prev) => {
        const next = new Set(prev);
        if (isEnabled) {
          next.delete(parameterId);
        } else {
          next.add(parameterId);
        }
        return next;
      });
    } catch (error) {
      console.error('Failed to toggle plan parameter:', error);
    }
  };

  const handleApplyDefaults = async () => {
    if (!selectedPlan) return;
    try {
      await adminApi.applyDefaultParameters(selectedPlan.id);
      const res = await adminApi.getPlanParameters(selectedPlan.id);
      const enabledIds = (res.data?.parameters || [])
        .filter((p: any) => p.isEnabledForPlan)
        .map((p: any) => p.id);
      setPlanParameters(new Set(enabledIds));
    } catch (error) {
      console.error('Failed to apply defaults:', error);
    }
  };

  // Filter parameters
  const filteredParameters = parameters.filter((p) => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (
        !p.code.toLowerCase().includes(q) &&
        !p.name.toLowerCase().includes(q) &&
        !(p.description?.toLowerCase().includes(q))
      ) {
        return false;
      }
    }
    if (filterCategory && p.category !== filterCategory) return false;
    if (filterStatus === 'active' && !p.isActive) return false;
    if (filterStatus === 'inactive' && p.isActive) return false;
    if (filterType === 'default' && !p.isDefault) return false;
    if (filterType === 'premium' && !p.isPremium) return false;
    return true;
  });

  // Group filtered parameters
  const filteredGrouped = filteredParameters.reduce((acc: Record<string, TrackerParameter[]>, p) => {
    if (!acc[p.category]) acc[p.category] = [];
    acc[p.category].push(p);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Paramètres Trackers</h1>
          <p className="text-gray-500 mt-1">
            Configurez les données disponibles par type d'abonnement
          </p>
        </div>
        <button
          onClick={openCreateModal}
          className="flex items-center gap-2 px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors"
        >
          <Plus className="h-5 w-5" />
          Nouveau paramètre
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-cyan-100 rounded-lg">
              <Settings2 className="h-5 w-5 text-cyan-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total paramètres</p>
              <p className="text-2xl font-bold text-gray-900">{parameters.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Check className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Actifs</p>
              <p className="text-2xl font-bold text-gray-900">
                {parameters.filter((p) => p.isActive).length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Star className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Par défaut</p>
              <p className="text-2xl font-bold text-gray-900">
                {parameters.filter((p) => p.isDefault).length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Crown className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Premium</p>
              <p className="text-2xl font-bold text-gray-900">
                {parameters.filter((p) => p.isPremium).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Plans Quick Access */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <h3 className="font-semibold text-gray-900 mb-3">Configuration par Plan</h3>
        <div className="flex flex-wrap gap-3">
          {plans.map((plan) => (
            <button
              key={plan.id}
              onClick={() => openPlanModal(plan)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <Settings2 className="h-4 w-4 text-gray-600" />
              <span className="font-medium">{plan.displayName}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher un paramètre..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
              />
            </div>
          </div>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-500"
          >
            <option value="">Toutes les catégories</option>
            {CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>
                {CATEGORY_LABELS[cat]}
              </option>
            ))}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as any)}
            className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-500"
          >
            <option value="all">Tous les statuts</option>
            <option value="active">Actifs</option>
            <option value="inactive">Inactifs</option>
          </select>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as any)}
            className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-500"
          >
            <option value="all">Tous les types</option>
            <option value="default">Par défaut</option>
            <option value="premium">Premium</option>
          </select>
        </div>
      </div>

      {/* Parameters by Category */}
      <div className="space-y-4">
        {CATEGORIES.filter((cat) => filteredGrouped[cat]?.length > 0).map((category) => (
          <div key={category} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {/* Category Header */}
            <button
              onClick={() => toggleCategory(category)}
              className={`w-full flex items-center justify-between p-4 ${CATEGORY_COLORS[category]} transition-colors`}
            >
              <div className="flex items-center gap-3">
                {CATEGORY_ICONS[category]}
                <span className="font-semibold">{CATEGORY_LABELS[category]}</span>
                <span className="text-sm opacity-75">({filteredGrouped[category]?.length || 0})</span>
              </div>
              {expandedCategories.has(category) ? (
                <ChevronDown className="h-5 w-5" />
              ) : (
                <ChevronRight className="h-5 w-5" />
              )}
            </button>

            {/* Parameters List */}
            {expandedCategories.has(category) && (
              <div className="divide-y divide-gray-100">
                {filteredGrouped[category]?.map((param) => (
                  <div
                    key={param.id}
                    className={`flex items-center justify-between p-4 hover:bg-gray-50 ${
                      !param.isActive ? 'opacity-50' : ''
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: param.color ? `${param.color}20` : '#f3f4f6' }}
                      >
                        <Settings2 className="h-5 w-5" style={{ color: param.color || '#6b7280' }} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{param.name}</span>
                          <code className="text-xs bg-gray-100 px-2 py-0.5 rounded text-gray-600">
                            {param.code}
                          </code>
                          {param.avlId && (
                            <span className="text-xs text-gray-400">AVL: {param.avlId}</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">{param.description || 'Aucune description'}</p>
                        <div className="flex items-center gap-2 mt-1">
                          {param.unit && (
                            <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded">
                              {param.unit}
                            </span>
                          )}
                          {param.isDefault && (
                            <span className="text-xs bg-amber-50 text-amber-600 px-2 py-0.5 rounded flex items-center gap-1">
                              <Star className="h-3 w-3" /> Par défaut
                            </span>
                          )}
                          {param.isPremium && (
                            <span className="text-xs bg-purple-50 text-purple-600 px-2 py-0.5 rounded flex items-center gap-1">
                              <Crown className="h-3 w-3" /> Premium
                            </span>
                          )}
                          {param.requiresObd && (
                            <span className="text-xs bg-orange-50 text-orange-600 px-2 py-0.5 rounded flex items-center gap-1">
                              <Plug className="h-3 w-3" /> OBD
                            </span>
                          )}
                          {param.requiresBluetooth && (
                            <span className="text-xs bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded flex items-center gap-1">
                              <Bluetooth className="h-3 w-3" /> BT
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleToggleActive(param)}
                        className={`p-2 rounded-lg transition-colors ${
                          param.isActive
                            ? 'text-green-600 hover:bg-green-50'
                            : 'text-gray-400 hover:bg-gray-100'
                        }`}
                        title={param.isActive ? 'Désactiver' : 'Activer'}
                      >
                        {param.isActive ? (
                          <ToggleRight className="h-5 w-5" />
                        ) : (
                          <ToggleLeft className="h-5 w-5" />
                        )}
                      </button>
                      <button
                        onClick={() => openEditModal(param)}
                        className="p-2 text-gray-400 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-colors"
                        title="Modifier"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(param)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                {editingParam ? 'Modifier le paramètre' : 'Nouveau paramètre'}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-500"
                    placeholder="ex: engine_rpm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-500"
                    placeholder="ex: Régime moteur"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-500"
                  rows={2}
                  placeholder="Description du paramètre..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Catégorie <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-500"
                    required
                  >
                    {CATEGORIES.map((cat) => (
                      <option key={cat} value={cat}>
                        {CATEGORY_LABELS[cat]}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sous-catégorie</label>
                  <input
                    type="text"
                    value={formData.subcategory}
                    onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-500"
                    placeholder="ex: temperature"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type de donnée</label>
                  <select
                    value={formData.dataType}
                    onChange={(e) => setFormData({ ...formData, dataType: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-500"
                  >
                    <option value="number">Nombre</option>
                    <option value="boolean">Booléen</option>
                    <option value="string">Texte</option>
                    <option value="json">JSON</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Unité</label>
                  <input
                    type="text"
                    value={formData.unit}
                    onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-500"
                    placeholder="ex: km/h, °C, %"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">AVL ID</label>
                  <input
                    type="number"
                    value={formData.avlId}
                    onChange={(e) => setFormData({ ...formData, avlId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-500"
                    placeholder="ex: 239"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Icône</label>
                  <input
                    type="text"
                    value={formData.icon}
                    onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-500"
                    placeholder="ex: Gauge"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Couleur</label>
                  <input
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="w-full h-10 border border-gray-200 rounded-lg cursor-pointer"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ordre</label>
                  <input
                    type="number"
                    value={formData.sortOrder}
                    onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-500"
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-4 pt-4 border-t border-gray-200">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="w-4 h-4 text-cyan-500 rounded focus:ring-cyan-500"
                  />
                  <span className="text-sm text-gray-700">Actif</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isDefault}
                    onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                    className="w-4 h-4 text-amber-500 rounded focus:ring-amber-500"
                  />
                  <span className="text-sm text-gray-700">Par défaut</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isPremium}
                    onChange={(e) => setFormData({ ...formData, isPremium: e.target.checked })}
                    className="w-4 h-4 text-purple-500 rounded focus:ring-purple-500"
                  />
                  <span className="text-sm text-gray-700">Premium</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.requiresObd}
                    onChange={(e) => setFormData({ ...formData, requiresObd: e.target.checked })}
                    className="w-4 h-4 text-orange-500 rounded focus:ring-orange-500"
                  />
                  <span className="text-sm text-gray-700">Requiert OBD</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.requiresBluetooth}
                    onChange={(e) => setFormData({ ...formData, requiresBluetooth: e.target.checked })}
                    className="w-4 h-4 text-indigo-500 rounded focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-700">Requiert Bluetooth</span>
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors"
                >
                  {editingParam ? 'Mettre à jour' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Plan Parameters Modal */}
      {showPlanModal && selectedPlan && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  Paramètres pour "{selectedPlan.displayName}"
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {planParameters.size} paramètres activés sur {parameters.filter((p) => p.isActive).length}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleApplyDefaults}
                  className="flex items-center gap-2 px-3 py-2 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                >
                  <RefreshCw className="h-4 w-4" />
                  Appliquer les défauts
                </button>
                <button
                  onClick={() => setShowPlanModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-6">
                {CATEGORIES.filter((cat) => groupedParameters[cat]?.some((p) => p.isActive)).map((category) => (
                  <div key={category}>
                    <div className={`flex items-center gap-2 mb-3 ${CATEGORY_COLORS[category]} px-3 py-2 rounded-lg`}>
                      {CATEGORY_ICONS[category]}
                      <span className="font-semibold">{CATEGORY_LABELS[category]}</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {groupedParameters[category]?.filter((p) => p.isActive).map((param) => (
                        <button
                          key={param.id}
                          onClick={() => handleTogglePlanParameter(param.id)}
                          className={`flex items-center gap-2 p-3 rounded-lg border transition-all ${
                            planParameters.has(param.id)
                              ? 'border-cyan-500 bg-cyan-50 text-cyan-700'
                              : 'border-gray-200 hover:border-gray-300 text-gray-600'
                          }`}
                        >
                          {planParameters.has(param.id) ? (
                            <Check className="h-4 w-4 text-cyan-500" />
                          ) : (
                            <div className="w-4 h-4 border border-gray-300 rounded" />
                          )}
                          <div className="flex-1 text-left">
                            <div className="font-medium text-sm">{param.name}</div>
                            {param.isPremium && (
                              <span className="text-xs text-purple-500">Premium</span>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <div className="flex justify-end">
                <button
                  onClick={() => setShowPlanModal(false)}
                  className="px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
